import moment from 'moment';
import { PeriodPreset, PeriodSelection } from 'views/Admin/UsageReport/types';

/**
 * Formats a Date as the seconds-precision UTC ISO string expected by the
 * backend's OffsetDateStringDeserializer ('yyyy-MM-ddTHH:mm:ssZ'). JS's
 * default Date.toISOString() includes milliseconds, which the deserializer
 * rejects.
 */
export const toApiIsoSeconds = (d: Date): string => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * Computes the actual { from, to } range a preset represents, mirroring
 * BzmDateRangePicker's fixedRangeSelectionHandler. This lets the trigger and
 * calendar display the real range while the API still receives the preset value.
 */
export const presetToRange = (preset: PeriodPreset): { from: Date; to: Date } => {
  const to: Date = new Date();
  const startOfDay = (m: moment.Moment): Date => m.startOf('day').toDate();
  switch (preset) {
    case 'LAST_MONTH':
      return { from: startOfDay(moment().subtract(1, 'month')), to };
    case 'LAST_6_MONTHS':
      return { from: startOfDay(moment().subtract(6, 'months')), to };
    case 'LAST_YEAR':
      return { from: startOfDay(moment().subtract(1, 'year')), to };
    case 'LAST_QUARTER':
    default:
      return { from: startOfDay(moment().subtract(3, 'months')), to };
  }
};

/** The { from, to } range to display in the date picker for the current selection. */
export const periodToDisplayRange = (period: PeriodSelection): { from: Date; to: Date } =>
  period.kind === 'custom' ? { from: period.from, to: period.to } : presetToRange(period.value);

/**
 * Serializes a custom { from, to } range for the API in UTC (the reporting backend and the
 * analytics rollups are UTC). The calendar day/month the user picked — read in local time — is
 * mapped to the same-numbered UTC day/month, so the range the backend sees matches what the
 * calendar shows regardless of the browser timezone. Without this, a local midnight in a UTC+
 * zone serialized to the previous day/month (e.g. local Jun 15 -> 2026-06-14T22:00:00Z).
 *
 * Monthly ranges (start older than 6 months) additionally snap to whole UTC months
 * (first-of-month 00:00 -> last-of-month 23:59:59) to line up with the monthly rollup buckets.
 */
export const customRangeToApi = (from: Date, to: Date): { from: string; to: string } => {
  if (isMonthlyRange(from)) {
    return {
      from: toApiIsoSeconds(new Date(Date.UTC(from.getFullYear(), from.getMonth(), 1, 0, 0, 0))),
      to: toApiIsoSeconds(new Date(Date.UTC(to.getFullYear(), to.getMonth() + 1, 0, 23, 59, 59))),
    };
  }
  return {
    from: toApiIsoSeconds(new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0))),
    to: toApiIsoSeconds(new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59))),
  };
};

/**
 * Ranges whose start is within this many months are shown at daily granularity (arbitrary
 * days); older starts use monthly granularity and must be whole-month aligned. Mirrors the
 * backend rule in AdminReportingService.resolutionFor / validateCustomRange.
 */
export const DAILY_GRANULARITY_MAX_MONTHS = 6;

/** The selectable window is limited to this many years back (monthly-rollup retention). */
export const MAX_RANGE_YEARS = 2;

/** Earliest date the pickers allow — {@link MAX_RANGE_YEARS} years back, start of day. */
export const minSelectableDate = (): Date =>
  moment()
    .subtract(MAX_RANGE_YEARS, 'years')
    .startOf('day')
    .toDate();

/** True when a range starting on {@code from} is served at monthly granularity (start older than 6 months). */
export const isMonthlyRange = (from: Date): boolean =>
  moment(from).isBefore(
    moment()
      .subtract(DAILY_GRANULARITY_MAX_MONTHS, 'months')
      .startOf('day'),
  );

/**
 * Snaps a monthly range (start older than 6 months) to whole-month boundaries so it lines up
 * with the monthly rollup buckets the backend requires; recent ranges are returned unchanged.
 */
export const snapRangeToGranularity = (range: { from: Date; to: Date }): { from: Date; to: Date } =>
  isMonthlyRange(range.from)
    ? {
        from: moment(range.from)
          .startOf('month')
          .toDate(),
        to: moment(range.to)
          .endOf('month')
          .toDate(),
      }
    : range;

/** Whole-month range for the LAST_YEAR preset: the last 12 months (first day of the earliest → end of the current month). */
export const lastYearWholeMonthRange = (): { from: Date; to: Date } => ({
  from: moment()
    .subtract(11, 'months')
    .startOf('month')
    .toDate(),
  to: moment()
    .endOf('month')
    .toDate(),
});
